import React, { useEffect, useRef } from 'react'
import clsx from 'clsx'
import { useQuery, useMutation } from '@apollo/client'
import { useHistory, useRouteMatch } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Switch from '@material-ui/core/Switch'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import MessageOutlinedIcon from 'components/material-symbol/icons/Message'
import LinkOutlinedIcon from 'components/material-symbol/icons/Link'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import CheckIcon from 'components/material-symbol/icons/Check'
import { PageHeader } from 'components/page-header'
import { FormActionBar } from 'components/form-util'
import { useSnackbar, useSnackbarSuccess, useSnackbarError } from 'lib/snackbar'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import * as yup from 'yup'
import useSettingsStyles, { SettingsFormControlLabel } from '../settings.styles'
import { SettingsSection, SettingsToggleRow } from '../settings-components'
import {
  LEAD_REPLY_PREFERENCES_QUERY,
  UPDATE_LEAD_REPLY_PREFERENCES,
} from '../queries'
import useStyles, { MESSAGE_MAX_LENGTH } from './styles'

const validationSchema = yup.object().shape({
  enabled: yup.boolean(),
  message: yup
    .string()
    .max(
      MESSAGE_MAX_LENGTH,
      `Message must be at most ${MESSAGE_MAX_LENGTH} characters`,
    )
    .required('Message is required'),
  includeBookingLink: yup.boolean(),
})

const LeadsAutoReplySettings = () => {
  const classes = useStyles()
  const settingsStyles = useSettingsStyles()
  const { path } = useRouteMatch()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const { data, loading, error } = useQuery(LEAD_REPLY_PREFERENCES_QUERY)
  const [updatePreferences, { loading: saving }] = useMutation(
    UPDATE_LEAD_REPLY_PREFERENCES,
  )

  const preferences = data?.currentUser?.attributes?.preferences || {}

  const original = useRef({
    enabled: preferences.leadAutoReplyEnabled || false,
    message: preferences.leadAutoReplyMessage || '',
    includeBookingLink: preferences.leadAutoReplyIncludeBookingLink || false,
  })

  const form = useForm({
    defaultValues: original.current,
    resolver: yupResolver(validationSchema),
  })
  const { control, handleSubmit, reset, watch, formState } = form
  const { isDirty, errors } = formState
  const message = watch('message') || ''

  useEffect(() => {
    const newVals = {
      enabled: preferences.leadAutoReplyEnabled || false,
      message: preferences.leadAutoReplyMessage || '',
      includeBookingLink: preferences.leadAutoReplyIncludeBookingLink || false,
    }
    original.current = newVals
    reset(newVals)
  }, [
    preferences.leadAutoReplyEnabled,
    preferences.leadAutoReplyMessage,
    preferences.leadAutoReplyIncludeBookingLink,
    reset,
  ])

  const crumbs = [
    {
      label: 'Settings',
      to: path
        .split('/')
        .slice(0, 3)
        .join('/'),
    },
  ]

  const onSubmit = async values => {
    try {
      await updatePreferences({
        variables: {
          input: {
            leadAutoReplyEnabled: values.enabled,
            leadAutoReplyMessage: values.message,
            leadAutoReplyIncludeBookingLink: values.includeBookingLink,
          },
        },
      })
      snackbarSuccess('Auto-reply settings saved!')
      history.goBack()
    } catch (e) {
      snackbarError(e.message)
    }
  }

  if (loading) return <LinearProgress color="primary" />
  if (error) {
    return <Typography color="error">Error loading settings.</Typography>
  }

  return (
    <>
      <PageHeader title="Leads Auto-Reply" crumbs={crumbs} />
      <form onSubmit={handleSubmit(onSubmit)}>
        {isDirty && (
          <FormActionBar
            variant="toolbar"
            includeCancel
            onCancel={() => history.goBack()}
            disableCancel={saving}
            loading={saving}
          >
            <Button
              type="submit"
              color="primary"
              variant="contained"
              startIcon={<CheckIcon fontSize="small" />}
              disabled={saving}
            >
              Save
            </Button>
          </FormActionBar>
        )}
        <Box
          className={clsx(
            settingsStyles.contentContainer,
            isDirty && settingsStyles.contentContainerWithStickySave,
          )}
        >
          <SettingsSection eyebrow="Status" classes={settingsStyles}>
            <Controller
              name="enabled"
              control={control}
              render={({ value, onChange }) => (
                <SettingsToggleRow
                  icon={<MessageOutlinedIcon />}
                  title="Send auto-reply to new leads"
                  description={
                    value
                      ? 'Active — new leads receive your message instantly.'
                      : 'Off — leads will not receive an automated reply.'
                  }
                  action={
                    <SettingsFormControlLabel
                      label=" "
                      control={
                        <Switch
                          checked={!!value}
                          onChange={e => onChange(e.target.checked)}
                          color="primary"
                          inputProps={{
                            'aria-label': 'Send auto-reply to new leads',
                          }}
                        />
                      }
                    />
                  }
                  classes={settingsStyles}
                />
              )}
            />
          </SettingsSection>

          <Box className={clsx(settingsStyles.section, classes.messageSection)}>
            <Box className={classes.sectionHeader}>
              <Typography
                component="h2"
                className={settingsStyles.sectionEyebrow}
              >
                Default message
              </Typography>
              <Typography component="p" className={classes.charCount}>
                {message.length} / {MESSAGE_MAX_LENGTH}
              </Typography>
            </Box>
            <Box
              className={`${settingsStyles.card} ${classes.messageCardContent}`}
            >
              <Controller
                name="message"
                control={control}
                render={({ value, onChange }) => (
                  <TextField
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    multiline
                    minRows={6}
                    fullWidth
                    variant="outlined"
                    className={classes.messageField}
                    inputProps={{ maxLength: MESSAGE_MAX_LENGTH }}
                    error={!!errors.message}
                    helperText={errors.message?.message}
                    FormHelperTextProps={{
                      style: errors.message ? undefined : { display: 'none' },
                    }}
                  />
                )}
              />
              <Box className={classes.messageFooter}>
                <InfoOutlinedIcon
                  className={classes.messageFooterIcon}
                  fontSize="small"
                />
                <Typography component="p" className={classes.messageFooterText}>
                  Keep your response friendly and professional. We&apos;ll send
                  it as soon as a lead contacts you.
                </Typography>
              </Box>
            </Box>
          </Box>

          <SettingsSection eyebrow="Options" classes={settingsStyles}>
            <Controller
              name="includeBookingLink"
              control={control}
              render={({ value, onChange }) => (
                <SettingsToggleRow
                  icon={<LinkOutlinedIcon />}
                  title="Include booking link"
                  description="Appends your Book Now link — PGA Coach calendar or external page, depending on your Book Now Preferences."
                  action={
                    <SettingsFormControlLabel
                      label=" "
                      control={
                        <Switch
                          checked={!!value}
                          onChange={e => onChange(e.target.checked)}
                          color="primary"
                          inputProps={{ 'aria-label': 'Include booking link' }}
                        />
                      }
                    />
                  }
                  classes={settingsStyles}
                />
              )}
            />
          </SettingsSection>

          <Typography component="p" className={classes.footerNote}>
            Changes apply to all new leads coming through your profile.
          </Typography>
        </Box>
      </form>
    </>
  )
}

export default LeadsAutoReplySettings
